import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Radio,
  RadioGroup,
  Typography,
} from "@mui/material";
import {
  Close as CloseIcon,
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
} from "@mui/icons-material";
import { baseAxios } from "../../utils/config";

function ClientCard({ client, selected, onChange }) {
  if (!client) return null;

  const name = [client.client_first_name, client.client_last_name]
    .filter(Boolean)
    .join(" ");
  const firstVisit = client.client_first_visit_date
    ? new Date(client.client_first_visit_date).toLocaleDateString()
    : "N/A";
  const lastVisit = client.client_last_visit_date
    ? new Date(client.client_last_visit_date).toLocaleDateString()
    : "N/A";

  return (
    <Box
      onClick={() => onChange(client.client_id)}
      sx={{
        border: selected ? "2px solid" : "1px solid",
        borderColor: selected ? "primary.main" : "grey.300",
        borderRadius: 2,
        p: 2,
        cursor: "pointer",
        bgcolor: selected ? "primary.50" : "transparent",
        "&:hover": { bgcolor: selected ? "primary.50" : "grey.50" },
        transition: "all 0.15s",
      }}
    >
      <FormControlLabel
        value={client.client_id}
        control={<Radio checked={selected} />}
        label={
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              {name || "Unnamed Client"}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Phone: {client.client_mobile_phone_number || "N/A"}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Email: {client.client_email || "N/A"}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              First visit: {firstVisit} &bull; Last visit: {lastVisit}
            </Typography>
          </Box>
        }
        sx={{ m: 0, width: "100%", alignItems: "flex-start" }}
      />
    </Box>
  );
}

export default function MergeDialog({ open, onClose, clients, onConfirm, loading }) {
  const [step, setStep] = useState(0);
  const [primaryId, setPrimaryId] = useState(null);
  const [clientDetails, setClientDetails] = useState([null, null]);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [fetchError, setFetchError] = useState(null);

  // Reset state when dialog opens
  useEffect(() => {
    if (open && clients?.length === 2) {
      setStep(0);
      setPrimaryId(null);
      setClientDetails([null, null]);
      setFetchError(null);
      setDetailsLoading(true);

      Promise.all([
        baseAxios.get(`/clients/${clients[0].client_id}`),
        baseAxios.get(`/clients/${clients[1].client_id}`),
      ])
        .then(([res1, res2]) => {
          setClientDetails([res1.data.client, res2.data.client]);
        })
        .catch(() => {
          setFetchError(
            "Failed to load client details. One or both clients may no longer exist."
          );
        })
        .finally(() => setDetailsLoading(false));
    }
  }, [open, clients]);

  const primaryClient = clientDetails.find((c) => c?.client_id === primaryId);
  const secondaryClient = clientDetails.find((c) => c?.client_id !== primaryId);

  const primaryName = primaryClient
    ? [primaryClient.client_first_name, primaryClient.client_last_name]
        .filter(Boolean)
        .join(" ")
    : "";
  const secondaryName = secondaryClient
    ? [secondaryClient.client_first_name, secondaryClient.client_last_name]
        .filter(Boolean)
        .join(" ")
    : "";

  const handleConfirm = () => {
    if (primaryId) onConfirm?.(primaryId);
  };

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2, overflow: "hidden" } }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          gap: 2,
          bgcolor: "secondary.main",
          color: "common.white",
          borderBottom: "1px solid",
          borderColor: "primary.main",
          px: 3,
          py: 2,
        }}
      >
        <Box>
          <Typography variant="h6" fontWeight={700} color="inherit">
            {step === 0
              ? "Merge clients — Step 1 of 2"
              : "Confirm merge — Step 2 of 2"}
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: "rgba(255, 255, 255, 0.75)", mt: 0.25 }}
          >
            {step === 0
              ? "Choose which client record to keep."
              : "Review the merge before confirming."}
          </Typography>
        </Box>
        <IconButton
          aria-label="close"
          onClick={onClose}
          size="small"
          disabled={loading}
          sx={{
            color: "primary.main",
            mt: 0.25,
            "&:hover": { bgcolor: "rgba(197, 160, 111, 0.16)" },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ px: 3, py: 2.5 }}>
        {fetchError ? (
          <Alert severity="error">{fetchError}</Alert>
        ) : detailsLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        ) : step === 0 ? (
          /* Step 0: Choose Primary */
          <>
            <Typography variant="body2" sx={{ mb: 2 }}>
              Select the client record to keep. All appointment history from both
              records will be combined under the selected client.
            </Typography>
            <RadioGroup
              value={primaryId ?? ""}
              onChange={(e) => setPrimaryId(Number(e.target.value))}
            >
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                {clientDetails.map(
                  (client) =>
                    client && (
                      <ClientCard
                        key={client.client_id}
                        client={client}
                        selected={primaryId === client.client_id}
                        onChange={setPrimaryId}
                      />
                    )
                )}
              </Box>
            </RadioGroup>
          </>
        ) : (
          /* Step 1: Confirm */
          <>
            <Alert severity="warning" sx={{ mb: 2 }}>
              This action cannot be undone.
            </Alert>
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Keep:</strong> {primaryName} ({primaryClient?.client_email || "no email"})
              </Typography>
              <Typography variant="body2">
                <strong>Remove:</strong> {secondaryName} ({secondaryClient?.client_email || "no email"})
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary">
              All appointments, receipts, and transactions from &ldquo;{secondaryName}&rdquo;
              will be transferred to &ldquo;{primaryName}&rdquo;. The duplicate record will be
              permanently removed.
            </Typography>
          </>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          py: 2,
          borderTop: "1px solid",
          borderColor: "divider",
          gap: 1,
        }}
      >
        {step === 1 && (
          <Button
            onClick={() => setStep(0)}
            startIcon={<ArrowBackIcon />}
            sx={{ mr: "auto", textTransform: "none" }}
            disabled={loading}
            color="secondary"
          >
            Back
          </Button>
        )}
        <Button
          onClick={onClose}
          disabled={loading}
          variant="outlined"
          color="secondary"
          sx={{ textTransform: "none", minWidth: 100 }}
        >
          Cancel
        </Button>
        {step === 0 ? (
          <Button
            variant="contained"
            color="primary"
            onClick={() => setStep(1)}
            disabled={!primaryId || detailsLoading || !!fetchError}
            endIcon={<ArrowForwardIcon />}
            sx={{
              textTransform: "none",
              fontWeight: 700,
              boxShadow: "none",
              "&:hover": { boxShadow: "none" },
            }}
          >
            Next
          </Button>
        ) : (
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirm}
            disabled={loading}
            startIcon={
              loading ? <CircularProgress size={16} color="inherit" /> : null
            }
            sx={{ textTransform: "none", fontWeight: 700 }}
          >
            Merge Clients
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
